import { FC, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../context/searchContext";
import useListFilters from "../../hooks/useListFilters";

import { useDebounce } from "../../hooks/useDebounce";
import useI18n from "../../hooks/useTranslations";
import { SearchItem } from "../../http/types/items";
import { useItemsApi } from "../../http/useItems";
import { PaginationQuery } from "../../types";
import FlexContent from "../ui/FlexContent";
import Header from "../ui/Header";
import SearchBar from "../ui/SearchBar";
import SectionCard from "../ui/SectionCard";
import Table from "../ui/Table";

const Items: FC = () => {
  return (
    <SearchContextProvider>
      <ItemsContent />
    </SearchContextProvider>
  );
};

const ItemsContent: FC = () => {
  const i18n = useI18n();
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const itemsApi = useItemsApi();
  const navigate = useNavigate();
  const { actions, setActions } = useSearchContext();
  const debouncedValue = useDebounce(filters.state.q);
  const searchPlaceholder = i18n.translation.items.search;

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = itemsApi.useItems(query, aborter.current.signal);

  useEffect(() => {
    setActions(
      <SearchBar
        placeholder={searchPlaceholder}
        value={filters.state.q ?? ""}
        onChange={(term) => {
          aborter.current.abort();
          aborter.current = new AbortController();
          filters.setSearchTerm(term);
        }}
      />,
    );

    return () => {
      setActions(null);
    };
  }, [filters.state.q, searchPlaceholder, setActions]);

  return (
    <>
      <Header
        title={i18n.translation.items.plural}
        path={{ display: i18n.translation.items.all }}
        error={error}
        buttons={actions}
      />

      <FlexContent padding>
        <SectionCard>
          <Table<SearchItem>
            filters={filters}
            isTall
            head={
              <tr>
                <th align="left">{i18n.translation.common.code}</th>
                <th align="left">{i18n.translation.common.description}</th>
              </tr>
            }
            items={data?.items}
            renderRow={(item) => (
              <>
                <td>
                  <Link to={`/items/${item.id}`} onClick={(e) => e.stopPropagation()}>
                    {item.code}
                  </Link>
                </td>
                <td>{item.description}</td>
              </>
            )}
            onRowClick={(item) => {
              navigate(`/items/${item.id}`);
            }}
            total={data?.total}
            error={!!error}
          />
        </SectionCard>
      </FlexContent>
    </>
  );
};

export default Items;
